"use client";
import Icon from "@/components/icons/Icons";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { motion } from "framer-motion";
import React from "react";
import CustomRangeSlider from "../ui/CustomRangeSlider";

interface ModeItem {
  key: "sip" | "lumpsum";
  label: string;
  icon: React.ReactNode;
}

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
  }),
};

type ComponentProps = {
  direction: number;
  tenure: number;
  error: boolean;
  setTenure: (v: number) => void;
  sipAmount: number;
  setSipAmount: (v: number) => void;
  mode: "sip" | "lumpsum";
  onChangeMode: (val: "sip" | "lumpsum") => void;
};

export default function InvestGoldFormStep1({ direction, tenure, error, setTenure, sipAmount, setSipAmount, mode, onChangeMode }: ComponentProps) {
  const suggestedAmounts = [1000, 2500, 5000];
  const MAX_AMOUNT = 100000000;

  const options: ModeItem[] = [
    {
      key: "sip",
      label: "Monthly SIP",
      icon: <Icon.sipTabIcon className="w-4 h-4" />,
    },
    {
      key: "lumpsum",
      label: "Lumpsum",
      icon: <Icon.durationIcon className="w-4 h-4" />,
    },
  ];

  const handleSipAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, "");
    let parsedValue = rawValue ? Number(rawValue) : 0;
    if (parsedValue > MAX_AMOUNT) parsedValue = MAX_AMOUNT;
    setSipAmount(parsedValue);
  };

  const handleAddAmount = (amt: number) => {
    const totalAmount = sipAmount + amt;
    setSipAmount(totalAmount);
  };

  return (
    <motion.div key="step1" variants={stepVariants} custom={direction} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: "easeOut" }} className="relative px-4 xs:px-6">
      <div className="mb-7 relative">
        <div className="text-lg xs:text-xl font-semibold text-white mb-3.5">Mode of Investment :</div>

        <div className="flex gap-3">
          {options.map((item) => {
            const isActive = mode === item.key;

            return (
              <button
                key={item.key}
                onClick={() => onChangeMode(item.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition duration-300 text-white
              ${isActive ? "border-[#F7A000A6] bg-[#DC940D26]" : "border-white/20 bg-transparent hover:border-white/60"}
            `}
              >
                <span className={isActive ? "text-[#F7A000A6]" : "text-white"}>{item.icon}</span>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-9 relative">
        <div className="text-lg xs:text-xl font-semibold text-white mb-3.5">Enter {mode === "sip" ? "SIP" : "Lumpsum"} Amount</div>

        <label className={`relative block py-1.5 px-3.5 xs:py-2.5 xs:px-6 text-xl xs:text-2xl font-semibold rounded-xl xs:rounded-2xl border bg-[#FFFFFF26] xs:border-2 transition mb-3 ${sipAmount < 100 ? "text-[#ec4d4d] border-[#ec4d4d]" : "border-[#FFFFFF26] hover:border-[#ffffff7c] text-white"}`}>
          <span className={`absolute left-4 xs:left-5 top-1/2 -translate-y-1/2 pointer-events-none transition ${sipAmount < 100 ? "text-[#ec4d4d]" : "text-white"}`}>₹</span>
          <input type="text" value={formatIndianNumber(sipAmount)} onChange={handleSipAmountChange} className={`px-4.5 w-full bg-transparent transition-all duration-200 ease-in-out ${sipAmount < 100 ? "text-[#ec4d4d] " : "text-white"}`} />
        </label>

        <div className="flex items-center gap-3">
          {suggestedAmounts.map((amt) => (
            <button key={amt} onClick={() => handleAddAmount(amt)} className="px-2 xs:px-3 py-1.5 border border-white/20 hover:border-white/60 rounded-lg xs:rounded-xl text-white font-medium text-xs xs:text-sm bg-transparent transition duration-300">
              + ₹ {amt.toLocaleString("en-IN")}
            </button>
          ))}
        </div>

        {sipAmount < 100 && <p className="absolute inset-x-0 text-left -bottom-7 text-[#FF6565] text-2xs">*Minimum amount is 100.</p>}
      </div>

      <div className="relative">
        <div className="text-lg xs:text-xl font-semibold text-white mb-0.5">Select your Tenure</div>

        <CustomRangeSlider className="flex flex-col sm:flex-row items-start sm:items-center justify-between" value={tenure} defaultIndex={0} values={[5, 10, 15, 20, 25, 30]} onChange={(value) => setTenure(value)} />
      </div>

      {error && <p className="absolute inset-x-0 text-center -bottom-20 text-[#FF6565] text-2xs">*Please enter valid values to proceed</p>}
    </motion.div>
  );
}
